import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ChatBubbleLeftIcon, InboxIcon } from '@heroicons/react/24/outline';
import Placeholder from '../../../ui/atoms/Placeholder';
import { PriorityPill } from '../../../ui';

const InboxSessionList = ({
  t,
  workspaceId,
  chatSessions,
  selectedSessionId,
  loading,
  initialLoading,
  isInboxRefreshing,
  currentPage,
  totalPages,
  totalCount,
  itemsPerPage,
  onRowClick,
  onPageChange
}) => {
  const pageStart = totalCount === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const pageEnd = totalCount === 0 ? 0 : Math.min(currentPage * itemsPerPage, totalCount);

  return (
    <div className="flex h-full max-h-full w-96 flex-shrink-0 flex-col overflow-hidden border-r border-neutral-200 bg-white min-h-0">
      <div className="flex-shrink-0 px-4 border-b border-neutral-200">
        {!workspaceId && !loading && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-4 w-4 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-xs text-yellow-700">
                  {t('assistant.inbox.noWorkspaceWarning')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="relative min-h-0 flex-1 overflow-y-auto assistant-scrollbar max-h-full">
        {isInboxRefreshing && (
          <div className="sticky top-0 z-10 flex items-center justify-center gap-2 border-b border-neutral-100 bg-white/95 py-1.5 text-xs text-gray-500">
            <div className="h-3 w-3 animate-spin rounded-full border-b-2 border-primary-500" />
            {t('assistant.inbox.refreshing')}
          </div>
        )}
        {(initialLoading || loading) && chatSessions.length === 0 ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="h-16 bg-gray-200 rounded" />
              </div>
            ))}
          </div>
        ) : chatSessions.length === 0 ? (
          <Placeholder
            empty
            size="m"
            icon={InboxIcon}
            message={t('assistant.startConversation')}
            description={t('assistant.inbox.emptyDescription')}
            className="h-full"
          />
        ) : (
          <div className="divide-y divide-gray-100">
            {chatSessions.map((session) => (
              <div
                key={session.id}
                className={`p-4 py-3 cursor-pointer transition-colors ${
                  selectedSessionId === session.id ? 'bg-primary-50' : 'hover:bg-neutral-50'
                }`}
                onClick={() => onRowClick(session)}
              >
                <div className="flex items-start">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-900 truncate pr-2">
                        {session.title && session.title.length > 45
                          ? `${session.title.substring(0, 45)}...`
                          : session.title || 'No title'}
                      </span>
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        {session.priority && (
                          <PriorityPill priority={session.priority} t={t} />
                        )}
                        <div className="flex items-center text-xs text-gray-500">
                          <ChatBubbleLeftIcon className="h-4 w-4 mr-1" />
                          {session.messageCount || 0}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {totalCount > 0 && (
        <div className="border-t border-gray-200 px-3 py-2.5 flex-shrink-0">
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage <= 1 || loading}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-neutral-200 text-gray-600 hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label={t('assistant.inbox.previousPage')}
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>

            <div className="min-w-0 flex-1 text-center">
              <p className="text-xs font-medium text-gray-700">
                {t('assistant.inbox.pageOf', { page: currentPage, total: totalPages })}
              </p>
              <p className="text-[11px] text-gray-500 truncate">
                {t('assistant.inbox.pageRange', { from: pageStart, to: pageEnd, total: totalCount })}
              </p>
            </div>

            <button
              type="button"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages || loading}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-neutral-200 text-gray-600 hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label={t('assistant.inbox.nextPage')}
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InboxSessionList;
